import { APIEmployee } from '../../backend'
import { MeasurLocale } from '../locale/i18n'
import { teamMemberUrl } from '../locale/pathing'

import {
  ContentfulMember,
  TeamMember,
  TeamMemberPageLink,
} from './shared-types'

const mergeEmployeeAndContentfulMember = (
  employee: APIEmployee,
  locale: MeasurLocale,
  teamMember?: ContentfulMember,
  teamMemberPage?: TeamMemberPageLink,
): TeamMember => {
  return {
    avatar: teamMember?.avatar ?? null,
    avatar20: teamMember?.avatar20 ?? null,
    avatar80: teamMember?.avatar80 ?? null,
    company: teamMember?.company ?? 'Measurlabs',
    customerFacingPhone: employee.customerFacingPhone,
    email: teamMember?.email ?? employee.googleAccount ?? null,
    id: employee.id,
    isActive: employee.isActive || false,
    linkedIn: teamMember?.linkedIn ?? null,
    name: {
      full: teamMember?.name ?? employee.name.full,
      short: employee.name.short,
    },
    portrait: teamMember?.portrait ?? null,
    position: teamMember?.position ?? null,
    profileUrl: teamMemberPage?.slug
      ? teamMemberUrl(locale, teamMemberPage.slug)
      : undefined,
    teams: teamMember?.teams ?? [],
    title: teamMember?.title ?? null,
  }
}

export const populateEmployeeWithContentfulData = (
  teamMembers: ContentfulMember[],
  employee: APIEmployee,
  teamMemberPageLinks: TeamMemberPageLink[],
  locale: MeasurLocale,
): TeamMember | undefined =>
  mergeEmployeeAndContentfulMember(
    employee,
    locale,
    teamMembers.find(tm => tm?.email === employee?.googleAccount),
    teamMemberPageLinks.find(
      tmpl => tmpl?.teamMember?.email === employee?.googleAccount,
    ),
  )

export const populateContentfulMemberWithEmployeeData = (
  contentfulMembers: ContentfulMember,
  employees: APIEmployee[],
  teamMemberPagesLinks: TeamMemberPageLink[],
  locale: MeasurLocale,
): TeamMember | undefined => {
  const employee = employees.find(
    e => e?.googleAccount === contentfulMembers?.email,
  )
  const teamMemberPage = teamMemberPagesLinks.find(
    tmpl => tmpl?.teamMember.email === contentfulMembers?.email,
  )

  if (!employee) {
    return undefined
  }

  return mergeEmployeeAndContentfulMember(
    employee,
    locale,
    contentfulMembers,
    teamMemberPage,
  )
}

export const activeTeamMembers = (
  contentfulMembers: ContentfulMember[],
  employees: APIEmployee[],
  teamMemberPages: TeamMemberPageLink[],
  locale: MeasurLocale,
): TeamMember[] => {
  const teamMembers = contentfulMembers.reduce<TeamMember[]>(
    (acc, contentfulMember) => {
      const employee = employees.find(
        e => e?.googleAccount === contentfulMember?.email,
      )
      if (employee?.isActive) {
        acc.push(
          mergeEmployeeAndContentfulMember(
            employee,
            locale,
            contentfulMember,
            teamMemberPages.find(
              tmpl => tmpl?.teamMember.email === contentfulMember?.email,
            ),
          ),
        )
        employees.splice(employees.indexOf(employee), 1)
      }
      return acc
    },
    [],
  )

  employees.forEach(employee => {
    if (employee?.isActive) {
      teamMembers.push(
        mergeEmployeeAndContentfulMember(
          employee,
          locale,
          undefined,
          undefined,
        ),
      )
    }
  })
  return teamMembers
}
